import OktaAuth, {
  AccessToken,
  IDToken,
  SessionObject,
  UserClaims,
} from '@okta/okta-auth-js';
import React, {createContext, useEffect, useMemo, useReducer} from 'react';
import {AuthLogin, UpdateState} from './AuthActions';
import AuthReducer, {AuthState, AuthProps} from './AuthReducer';
import authClient from './config';
import {Buffer} from 'buffer';
import {
  authLogin,
  authLogout,
  isTokenValid,
  refreshSession,
  refreshToken,
} from './AuthHelpers';
import {APP_LOGOUT_URL} from '../config/getEnvironmentVars';

interface AuthContextTypes {
  accessToken?: AccessToken;
  authClient: OktaAuth;
  authLogin: (logoutURL?: string) => void;
  authLogout: () => void;
  idToken?: IDToken;
  initialState?: AuthProps;
  isAuthenticated: boolean;
  refreshAuthSession: () => void;
  refreshAuthToken: () => void;
  session?: SessionObject;
  user?: UserClaims;
}

export const AuthContext = createContext<AuthContextTypes>({
  accessToken: undefined,
  authClient,
  authLogin: () => {},
  authLogout: () => {},
  idToken: undefined,
  initialState: undefined,
  isAuthenticated: false,
  refreshAuthSession: () => {},
  refreshAuthToken: () => {},
  session: undefined,
  user: undefined,
});

const AuthProvider = ({children}) => {
  const [state, dispatch] = useReducer(AuthReducer, AuthState as AuthProps);

  const setAuthListeners = async () => {
    // Subscribe to authState change event.
    authClient.authStateManager.subscribe((authState) => {
      // Logic based on authState is done here.
      if (!authState.isAuthenticated) {
        // render unathenticated view
        return;
      }

      // Render authenticated view
      AuthLogin(dispatch, authState);
    });

    // Handle callback
    if (authClient.token.isLoginRedirect()) {
      const {state, tokens} = await authClient.token.parseFromUrl(); // remember to "await" this async call
      authClient.tokenManager.setTokens(tokens);
      UpdateState(dispatch, Buffer.from(state, 'base64').toString());
    }

    // normal app startup
    authClient.start(); // will update auth state and call event listeners
  };

  useEffect(() => {
    setAuthListeners();
    return () => {
      authClient.stop();
    };
  }, []);

  useEffect(() => {
    if (state.user !== undefined) {
      isTokenValid(state, authClient, dispatch);
    }
  }, [state.user]);

  const authContextValues = useMemo(
    () => ({
      authClient,
      authLogin: () => authLogin(dispatch, state, authClient),
      authLogout: (logoutURL = APP_LOGOUT_URL) =>
        authLogout(dispatch, authClient, logoutURL),
      refreshAuthSession: () => refreshSession(dispatch, authClient),
      refreshAuthToken: () => refreshToken(authClient),
      ...state,
    }),
    [state],
  );

  return (
    <AuthContext.Provider value={authContextValues}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
